/**
 * The most recent reading of one field across a run of observations.
 *
 * NDBC fills a row whenever the anemometer reports, and leaves `MM` in every
 * column whose sensor had nothing to say at that instant. The sensors are not
 * synchronised: wave height arrives roughly hourly, and water temperature drops
 * out of individual transmissions for no visible reason. Monterey at 00:40 UTC
 * carried wind and air temperature but `MM` for both water temperature and wave
 * height, while 00:30 had all four.
 *
 * So reading a field off the newest row alone reports "no data" for a sensor
 * that is working perfectly and answered ten minutes ago. Each field has to be
 * followed back to its own last real value.
 *
 * Bounded, because "last known" without a limit would show a sensor that failed
 * this morning as though it were current.
 */

export interface Timestamped {
  /** ISO instant, as the proxy returns it. */
  t: string;
}

/**
 * How far back a value may be taken from and still be shown as current.
 *
 * Two hours: wave height is reported about hourly, so one missed report plus
 * NDBC's publication lag already accounts for most of it. Water and air
 * temperature drift slowly enough over that span that the alternative — a dash
 * where a good reading exists — is the worse answer.
 */
export const MAX_VALUE_AGE_MS = 120 * 60_000;

export function mostRecentValue<T extends Timestamped>(
  /** Oldest first, as stored. */
  rows: readonly T[],
  pick: (row: T) => number | null,
  maxAgeMs: number = MAX_VALUE_AGE_MS,
  now: Date = new Date(),
): number | null {
  const floor = now.getTime() - maxAgeMs;

  for (let i = rows.length - 1; i >= 0; i--) {
    const row = rows[i];
    const at = Date.parse(row.t);
    // Rows are ordered, so the first one too old ends the search.
    if (Number.isFinite(at) && at < floor) break;

    const value = pick(row);
    if (value !== null && Number.isFinite(value)) return value;
  }

  return null;
}
